/**
 * Design System Constants
 * Consistent spacing, sizing, and styling across all modules
 */

// Semantic color system for status indicators
export const semanticColors = {
  success: {
    bg: 'bg-green-50',
    border: 'border-green-200',
    borderLeft: 'border-l-4 border-green-600',
    text: 'text-green-700',
    textDark: 'text-green-800',
    badge: 'bg-green-100 text-green-800',
    icon: 'text-green-600'
  },
  warning: {
    bg: 'bg-yellow-50',
    border: 'border-yellow-200',
    borderLeft: 'border-l-4 border-yellow-500',
    text: 'text-yellow-700',
    textDark: 'text-yellow-800',
    badge: 'bg-yellow-100 text-yellow-800',
    icon: 'text-yellow-600'
  },
  error: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    borderLeft: 'border-l-4 border-red-600',
    text: 'text-red-700',
    textDark: 'text-red-800',
    badge: 'bg-red-100 text-red-800',
    icon: 'text-red-600'
  },
  info: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    borderLeft: 'border-l-4 border-blue-600',
    text: 'text-blue-700',
    textDark: 'text-blue-800',
    badge: 'bg-blue-100 text-blue-800',
    icon: 'text-blue-600'
  },
  neutral: {
    bg: 'bg-gray-50',
    border: 'border-gray-200',
    borderLeft: 'border-l-4 border-gray-400',
    text: 'text-gray-700',
    textDark: 'text-gray-800',
    badge: 'bg-gray-100 text-gray-800',
    icon: 'text-gray-500'
  }
};

// Card pattern templates for consistent design
export const cardPatterns = {
  // Standard card - white background, subtle shadow
  standard: {
    container: 'bg-white shadow-sm rounded-lg',
    header: 'px-5 py-3 border-b border-gray-200',
    headerTitle: 'text-base font-medium text-gray-900',
    body: 'p-4'
  },
  // Status card - white with colored left border
  statusCard: {
    container: (status) => `bg-white shadow-sm rounded-lg ${semanticColors[status]?.borderLeft || ''}`,
    header: 'px-5 py-3 border-b border-gray-200',
    headerTitle: 'text-base font-medium text-gray-900',
    body: 'p-4'
  },
  // Alert item - subtle colored background inside cards
  alertItem: {
    container: (status) => `flex items-start space-x-3 p-3 rounded-md border ${semanticColors[status]?.border || ''} ${semanticColors[status]?.bg || ''}`,
    icon: (status) => `h-5 w-5 flex-shrink-0 ${semanticColors[status]?.icon || ''}`,
    title: 'text-sm font-medium text-gray-900',
    description: 'text-xs text-gray-500'
  },
  // Badge - small colored indicator
  badge: {
    base: 'px-2.5 py-0.5 rounded-full text-xs font-medium',
    status: (status) => semanticColors[status]?.badge || 'bg-gray-100 text-gray-800'
  }
};

export const DESIGN = {
  // Card Headers
  header: {
    padding: 'px-5 py-3',
    borderBottom: 'border-b border-gray-200',
    title: 'text-base font-medium text-gray-900',
    subtitle: 'text-xs text-gray-500'
  },
  
  // Card Body
  body: {
    padding: 'p-4',
    paddingLarge: 'p-5',
    spacing: 'space-y-4'
  },
  
  // Buttons
  button: {
    padding: 'px-3 py-2',
    paddingSmall: 'px-2 py-1',
    text: 'text-sm',
    textSmall: 'text-xs'
  },
  
  // Quick Action Cards
  quickAction: {
    container: 'p-3 rounded-lg border border-gray-200',
    icon: 'h-4 w-4',
    iconContainer: 'p-1.5 rounded-lg',
    label: 'text-xs font-medium text-gray-900',
    description: 'text-xs text-gray-400'
  },
  
  // Grid Layouts
  grid: {
    twoCol: 'grid grid-cols-2 gap-3',
    threeCol: 'grid grid-cols-3 gap-3',
    fourCol: 'grid grid-cols-4 gap-2',
    responsive2to3: 'grid grid-cols-2 lg:grid-cols-3 gap-3'
  },
  
  // List Items
  listItem: {
    padding: 'px-5 py-3',
    hover: 'hover:bg-gray-50',
    divider: 'divide-y divide-gray-200'
  },
  
  // Text Sizes
  text: {
    moduleTitle: 'text-2xl font-bold text-gray-900',
    moduleSubtitle: 'text-sm text-gray-500',
    cardTitle: 'text-base font-medium text-gray-900',
    cardSubtitle: 'text-xs text-gray-500',
    label: 'text-sm font-medium text-gray-700',
    value: 'text-sm text-gray-900',
    helper: 'text-xs text-gray-500'
  },
  
  // Icons
  icon: {
    small: 'h-3 w-3',
    medium: 'h-4 w-4',
    large: 'h-5 w-5'
  },
  
  // Spacing
  spacing: {
    section: 'mb-6',
    moduleHeader: 'mb-8',
    cardGap: 'gap-6',
    itemGap: 'gap-3',
    tightGap: 'gap-2'
  },
  
  // Navigation Components (NEW)
  navigation: {
    backButton: {
      icon: 'ArrowLeft',
      size: 'h-5 w-5',
      padding: 'p-1.5',
      hoverBg: 'hover:bg-slate-100',
      textColor: 'text-slate-600 hover:text-slate-900'
    },
    closeButton: {
      icon: 'X',
      size: 'h-6 w-6',
      textColor: 'text-gray-400 hover:text-gray-500'
    },
    header: {
      padding: 'px-6 py-4',
      borderBottom: 'border-b border-gray-200',
      titleSize: 'text-lg font-semibold text-gray-900',
      subtitleSize: 'text-sm text-gray-500'
    },
    swipe: {
      threshold: 50,
      edgeWidth: 20
    }
  }
};

export default DESIGN;